import { isShallowEqual } from "@suse-o11y/is-shallow-equal";
import type { JSON } from "@suse-o11y/json";
import type { PickReadonly } from "@suse-o11y/pick-readonly";
import { z } from "zod";

import { type Setter, resolve } from "../../generic/setter";
import type { PersesGridItem } from "../openapi";

export type DashboardLayoutSpecConfig = PickReadonly<Omit<DashboardLayoutSpec, "update" | "isEqual">>;

export type DashboardLayoutSpecInitConfig = Partial<DashboardLayoutSpecConfig>;

export class DashboardLayoutSpec {
	public static schema = z
		.object({
			x: z.number(),
			y: z.number(),
			width: z.number(),
			height: z.number(),
			content: z.object({
				$ref: z
					.string()
					.transform((value) => value.split("/").at(-1))
					.pipe(z.string()),
			}),
		})
		.transform(({ x, y, width, height, content: { $ref: id } }): [string, DashboardLayoutSpec] => [
			id,
			new DashboardLayoutSpec(x, y, width, height),
		]);

	public static of({ x = 0, y = 0, width = 1, height = 1 }: DashboardLayoutSpecInitConfig = {}): DashboardLayoutSpec {
		return new DashboardLayoutSpec(x, y, width, height);
	}

	private constructor(
		public readonly x: number,
		public readonly y: number,
		public readonly width: number,
		public readonly height: number,
	) {}

	public update(setter: Setter<Partial<DashboardLayoutSpecConfig>, [DashboardLayoutSpec]>): DashboardLayoutSpec {
		const { x = this.x, y = this.y, width = this.width, height = this.height } = resolve(setter, this);

		const updated = new DashboardLayoutSpec(x, y, width, height);

		return this.isEqual(updated) ? this : updated;
	}

	public isEqual(another: DashboardLayoutSpec): boolean {
		return isShallowEqual(another, this);
	}

	public toPerses(id: string): PersesGridItem {
		return {
			x: this.x,
			y: this.y,
			width: this.width,
			height: this.height,
			content: {
				$ref: `#/spec/panels/${id}`,
			},
		} satisfies JSON;
	}
}
